import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppConstants } from '../../../../app-constants.service';
import { ValidatorInput } from '../../../../model.module/validation/validation-descriptor.entity';
import { MessageObservable } from '../../../../services.module/message.observable';
import { ErhoeheKreditlinieModel } from '../../../../model.module/xhr/erhoehe-kreditlinie.model';
import { SortFunctions } from '../../../../pipes.module/_sort.functions';
import { MessageModel } from '../../../../model.module/modals.model';
import {
    type ErhoeheKreditlinieRequest,
    Notification,
    NotificationType,
    PartnerProfilData
} from '../../../../../generated/api/kreditportalBackendAPI.schemas';
import { VertraegeService } from '../../../../providers.module/vertraege.service';
import { JsonFilesApiService } from '../../../../../generated/api/json-files/json-files.service';
import { PartnerService } from '../../../../providers.module/partner.service';
import { KreditlinieStatic } from '../../../../model.module/json/kreditlinie-static.model';
import { KreditlinieStaticEntry } from '../../../../model.module/json/kreditlinie-static-entry.model';
import { KreditlinieRuled } from '../../../../model.module/json/kreditlinie-ruled.model';
import { ProduktlinieVhvKaution } from '../../../../model.module/json/produktlinie-vhv-kaution.model';
import { KreditlinieVhvKautionEntry } from '../../../../model.module/json/kreditlinie-vhv-kaution-entry.model';
import { ProduktlinieVhvKautionEntry } from '../../../../model.module/json/produktlinie-vhv-kaution-entry.model';
import { KreditlinieRuledProduktlinien } from '../../../../model.module/json/kreditlinie-ruled-produktlinie.enum';
import { KreditlinieApiService } from '../../../../../generated/api/kreditlinie/kreditlinie.service';
import { Subscription } from 'rxjs';

@Component({
    selector: 'vertrag-aendern',
    templateUrl: './vertrag-aendern.component.html',
    styleUrls: ['./vertrag-aendern.component.less'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class VertragAendernComponent implements OnInit, OnDestroy {

    private readonly meldungHoechsteLinie = 'Sie nutzen bereits die größtmögliche Linie innerhalb des gewählten Produktes. ' +
        'Bitte teilen Sie uns Ihren zusätzlichen Bürgschaftsbedarf mit - gerne prüfen wir für Sie ein individuelles Angebot.';

    model: ErhoeheKreditlinieModel;

    page1 = true;
    page2 = false;
    page3 = false;

    beitragNotification: Notification;
    keineErhoehungNotification: Notification;

    partner: PartnerProfilData = null;

    private motorInterval;

    private partnerServiceSub: Subscription;

    constructor(
        private readonly router: Router,
        private readonly appConstants: AppConstants,
        private readonly activatedRoute: ActivatedRoute,
        private readonly messageObservable: MessageObservable,
        private readonly vertraegeService: VertraegeService,
        private readonly partnerService: PartnerService,
        private readonly jsonFilesApiService: JsonFilesApiService,
        private readonly kreditlinieApiService: KreditlinieApiService
    ) {
        this.partnerServiceSub = this.partnerService.getPartner().subscribe(partner => {
            this.partner = partner;
        });
    }

    ngOnDestroy(): void {
        if (this.partnerServiceSub) {
            this.partnerServiceSub.unsubscribe();
        }
    }

    ngOnInit(): void {
        this.model = new ErhoeheKreditlinieModel(this.appConstants);

        this.activatedRoute.params.subscribe(params => {
            const tmp = params['subPage'];
            if (tmp) {
                this.loadVertrag(tmp);
            }
        });
    }

    nextPage() {
        this.page1 = false;
        this.page2 = true;
    }

    nextPageStatic() {
        const validationResult = this.model.validate('geschaeftsjahr');
        const valid = validationResult === null;

        if (this.model.staticTable.kreditlinien.length === 0) {
            this.messageObservable.notify(new MessageModel(
                'Kreditlinie kann nicht erhöht werden',
                [this.meldungHoechsteLinie],
            ));

            return;
        }

        if (valid) {
            this.nextPage();
        }
    }

    nextPageRuled() {
        const validationResult = this.model.validate('buergschaftslinieEingabe');
        const valid = validationResult === null;

        if (valid) {
            this.nextPage();
        }
    }

    nextPageVhvKaution() {
        const validationResult = this.model.validate('geschaeftsjahr');
        const valid = validationResult === null;

        if (this.model.vhvKautionTable.kreditlinien.length === 0) {
            this.messageObservable.notify(new MessageModel(
                'Kreditlinie kann nicht erhöht werden',
                [this.meldungHoechsteLinie],
            ));

            return;
        }

        if (valid) {
            this.nextPage();
        }
    }

    nextPageIndividual() {
        const validationResult = this.model.validate('buergschaftbetrag');
        const valid = validationResult === null;

        if (valid) {
            this.nextPage();
        }
    }

    previousPage() {
        this.page1 = true;
        this.page2 = false;
    }

    doBeantragen() {
        const validationResult = this.model.validate();
        const valid = validationResult === null;

        if (!valid) {
            return;
        }

        this.model.confirmationDate = new Date().toLocaleString();

        this.sendKreditlinienErhoehungV2();
    }

    displayBeitragRuledMessage(): void {
        if (!this.model.buergschaftslinieEingabe.valid) {
            return;
        }

        let differenzBeitrag = 0;

        if (this.model.produktlinie !== this.model.produktlinieAuswahl) {
            const alterBetrag = this.model.ruledData.getBeitrag(
                this.model.produktlinie,
                this.model.currVertrag.rahmenBetrag
            );

            const neuerBetrag = this.model.ruledData.getBeitrag(
                this.model.produktlinieAuswahl,
                this.model.currVertrag.rahmenBetrag
            );

            differenzBeitrag = neuerBetrag - alterBetrag;
        }

        const beitrag: number = this.model.ruledData.getBeitrag(
            this.model.produktlinieAuswahl,
            this.model.getBuergschaftslinieEingabe()
        ) + differenzBeitrag;

        const beitragFormatted = beitrag.formatMoney(2, 3, '.', ',');

        this.beitragNotification = {
            type: NotificationType.info,
            message: `<strong>INFO: </strong> Diese Erhöhung kostet Sie <strong>${beitragFormatted} €</strong>`,
        };
    }

    formatMoneyInput(input: ValidatorInput) {
        let val = input.value;

        if (val.indexOf) {
            const firstComma = val.indexOf(',');

            if (firstComma !== -1) {
                val = val.substring(0, firstComma);
            }

            val = val.replace(/\./g, '');
        }

        if (isNaN(val)) {
            input.valid = false;
            input.errorMsg = 'Dies ist keine gültige Zahl.';
            return;
        }

        input.valid = true;

        const numVal = Number(val);
        input.value = numVal.formatMoney(0, 3, '.', ',');

        this.model.validateField(input);
    }

    triggerAmountChange(evt) {
        this.formatMoneyInput(this.model.buergschaftslinieEingabe);
        this.displayBeitragRuledMessage();
    }

    triggerAmountChangeIndividual(evt) {
        this.formatMoneyInput(this.model.buergschaftbetrag);
    }

    startMotorPrev() {
        this.motorInterval = window.setInterval(
            () => this.previousRuledBuergschaftslinie(),
            140
        );
    }

    startMotorNext() {
        this.motorInterval = window.setInterval(
            () => this.nextRuledBuergschaftslinie(),
            140
        );
    }

    stopMotor() {
        window.clearInterval(this.motorInterval);
    }

    getDifferenzStatic(): number {
        return this.model.getDifferenzStatic();
    }

    private displayStatic(data: KreditlinieStatic): void {
        data = new KreditlinieStatic().doDeserialize(data);

        SortFunctions.sortArray4Number(data.kreditlinien, 'buergschaftsLinie', true);
        this.model.staticTable = data;
        const tmp: KreditlinieStaticEntry[] = [];

        for (const line of data.kreditlinien) {
            if (line.buergschaftsLinie > this.model.currVertrag.rahmenBetrag) {
                tmp.push(line);
            }
        }

        this.model.staticTable.kreditlinien = tmp;
    }

    private displayRuled(data: KreditlinieRuled, produktlinie: string): void {
        this.model.ruledData = new KreditlinieRuled().doDeserialize(data);
        this.model.setBuergschaftslinieEingabe(0);
        this.model.produktlinie = produktlinie;
        this.model.produktlinieAuswahl = produktlinie;
    }

    private displayVhvKaution(result: ProduktlinieVhvKaution, produktlinie: string, isProduktlinienSwitch = false) {
        result = new ProduktlinieVhvKaution().doDeserialize(result);
        const produktlinieVhvKautionEntry = result.produktlinien.find(value => value.produktlinieName === produktlinie);

        SortFunctions.sortArray4Number(produktlinieVhvKautionEntry.kreditlinien, 'buergschaftsLinie', true);

        this.model.vhvKautionTable = produktlinieVhvKautionEntry;

        if (!this.model.vhvKautionNextProduktlinien) {
            this.model.vhvKautionNextProduktlinien = this.model.vhvKautionTable.getNextProduktlinien();
        }

        let tmp: KreditlinieVhvKautionEntry[];

        if (isProduktlinienSwitch) {
            tmp = this.createErhoehungsAuswahlElementeWithSwitch(produktlinieVhvKautionEntry);
        } else {
            tmp = this.createErhoehungsAuswahlElemente(produktlinieVhvKautionEntry);
        }

        this.model.vhvKautionTable.kreditlinien = tmp;

        this.model.produktlinie = produktlinie;
        this.model.produktlinieAuswahl = produktlinie;
    }

    private createErhoehungsAuswahlElemente(produktlinieVhvKautionEntry: ProduktlinieVhvKautionEntry): KreditlinieVhvKautionEntry[] {
        const result: KreditlinieVhvKautionEntry[] = [];

        for (const line of produktlinieVhvKautionEntry.kreditlinien) {
            if (line.buergschaftsLinie > this.model.currVertrag.rahmenBetrag) {
                result.push(line);
            }
        }

        return result;
    }

    private createErhoehungsAuswahlElementeWithSwitch(produktlinieVhvKautionEntry: ProduktlinieVhvKautionEntry): KreditlinieVhvKautionEntry[] {
        const result: KreditlinieVhvKautionEntry[] = [];

        for (const line of produktlinieVhvKautionEntry.kreditlinien) {
            if (line.buergschaftsLinie >= this.model.currVertrag.rahmenBetrag) {
                result.push(line);
            }
        }

        return result;
    }

    private displayIndividual(): void {
        this.model.individual = true;
    }

    private displayKeineErhoehung(): void {
        this.model.keineErhoehung = true;

        this.keineErhoehungNotification = {
            message: 'Es ist keine Kreditlinienerhöhung möglich.',
            type: NotificationType.warning
        };
    }

    public previousRuledBuergschaftslinie(): void {
        const nextValue: number = this.model.ruledData.previous(
            this.model.produktlinieAuswahl,
            this.model.currVertrag.rahmenBetrag + this.model.getBuergschaftslinieEingabe(),
        );

        this.checkMinimalUndMaximalHoehe(nextValue);
        this.formatMoneyInput(this.model.buergschaftslinieEingabe);
        this.displayBeitragRuledMessage();
    }

    public nextRuledBuergschaftslinie(): void {
        const nextValue: number = this.model.ruledData.next(
            this.model.produktlinieAuswahl,
            this.model.currVertrag.rahmenBetrag + this.model.getBuergschaftslinieEingabe(),
        );

        this.model.ruledData.getCurrentProduktlinie(this.model.produktlinieAuswahl);

        this.checkMinimalUndMaximalHoehe(nextValue);
        this.formatMoneyInput(this.model.buergschaftslinieEingabe);
        this.displayBeitragRuledMessage();
    }

    public validateRuledBuergschaftslinie(): void {
        if (!this.model.buergschaftslinieEingabe.valid) {
            return;
        }

        this.formatMoneyInput(this.model.buergschaftslinieEingabe);
        this.displayBeitragRuledMessage();
    }

    public checkMinimalUndMaximalHoehe(nextValue: number): void {
        const produktlinie = this.model.ruledData.getCurrentProduktlinie(
            this.model.produktlinieAuswahl
        );

        // Prüfen, ob Eingabe niedriger als aktueller Wert
        if (nextValue < this.model.currVertrag.rahmenBetrag) {
            this.model.setBuergschaftslinieEingabe(0);
        } else if (nextValue > produktlinie.maximalHoehe) {
            this.model.setBuergschaftslinieEingabe(produktlinie.maximalHoehe - this.model.currVertrag.rahmenBetrag);
        } else {
            this.model.setBuergschaftslinieEingabe(nextValue - this.model.currVertrag.rahmenBetrag);
        }
    }

    public checkProduktlinieAuswahl(): void {
        console.debug('Switch der Produktlinie, neue Auswahl: ' + this.model.produktlinieAuswahl);
        const produktlinie = this.model.ruledData.getCurrentProduktlinie(this.model.produktlinieAuswahl);

        // nur bei Standard -> Start, Maximalwert setzen
        if (produktlinie.next === 0) {
            this.model.setBuergschaftslinieEingabe(produktlinie.maximalHoehe - this.model.currVertrag.rahmenBetrag);
            this.formatMoneyInput(this.model.buergschaftslinieEingabe);
            this.displayBeitragRuledMessage();
        }

        // nur bei Start -> Standard, Spezial, Startwert setzen
        if ((this.model.currVertrag.rahmenBetrag + this.model.getBuergschaftslinieEingabe()) < produktlinie.start) {
            this.model.setBuergschaftslinieEingabe(produktlinie.start - this.model.currVertrag.rahmenBetrag);
            this.formatMoneyInput(this.model.buergschaftslinieEingabe);
            this.displayBeitragRuledMessage();
        }

        this.displayBeitragRuledMessage();
    }

    public checkProduktlinieVhvKautionAuswahl(): void {
        console.debug('Switch der Produktlinie VHV-Kaution, neue Auswahl: ' + this.model.produktlinieAuswahl);

        const jsonFileName = this.appConstants.jsonBusinessFiles.produktlinieVhvKaution;

        this.jsonFilesApiService.getJsonFile(jsonFileName).subscribe((result: any) => {
            this.displayVhvKaution(result, this.model.produktlinieAuswahl, true);
        });
    }

    private getOptions(vertragsArt: string, modul: string, vsnr: string): void {
        console.debug(
            `Erhöhung der Kreditlinie wird durch folgende Parameter festgelegt:
            [vertragsArt=${vertragsArt}, modul=${modul}, vsnr=${vsnr}]`
        );

        switch (vertragsArt) {
            case 'Rahmenvertrag':
                this.handleRahmenvertrag(modul);
                break;
            case 'VHV Kaution':
                this.handleVhvKaution(modul);
                break;
            case 'KTV Premium':
                this.handleKtvPremium(vsnr);
                break;
            case 'KTV Gewerbe und Industrie':
                this.handleKtvGewerbeUndIndustrie();
                break;
            case 'KTV Premium Austria':
            case 'KTV Bau-Handwerk':
                this.displayKeineErhoehung();
                break;
            default:
                this.handleSonstigeVertragsart(vsnr);
                break;
        }

        console.debug(
            `Erhoehung der Kreditlinie gesetzt
            [individual=${this.model.individual}, keineErhoehung=${this.model.keineErhoehung},
            staticTable=${this.model.staticTable}, ruledData=${this.model.ruledData}`
        );
    }

    private handleRahmenvertrag(modul: string) {
        const jsonFileName = this.appConstants.jsonBusinessFiles.produktlinieRahmenvertrag;
        if (modul === 'Start') {
            this.jsonFilesApiService.getJsonFile(jsonFileName).subscribe((result: any) => {
                this.displayRuled(result, KreditlinieRuledProduktlinien.START);
            });
        } else if (modul === 'Standard') {
            this.jsonFilesApiService.getJsonFile(jsonFileName).subscribe((result: any) => {
                this.displayRuled(result, KreditlinieRuledProduktlinien.STANDARD);
            });
        } else if (modul === 'Spezial') {
            this.jsonFilesApiService.getJsonFile(jsonFileName).subscribe((result: any) => {
                this.displayRuled(result, KreditlinieRuledProduktlinien.SPEZIAL);
            });
        } else {
            this.displayIndividual();
        }
    }

    private handleVhvKaution(modul: string) {
        console.debug(`VHV-Kaution erkannt [modul=${modul}]`);

        const jsonFileName = this.appConstants.jsonBusinessFiles.produktlinieVhvKaution;

        if (modul === 'Start') {
            this.jsonFilesApiService.getJsonFile(jsonFileName).subscribe((result: any) => {
                this.displayVhvKaution(result, KreditlinieRuledProduktlinien.START);
            });
        } else if (modul === 'Standard') {
            this.jsonFilesApiService.getJsonFile(jsonFileName).subscribe((result: any) => {
                this.displayVhvKaution(result, KreditlinieRuledProduktlinien.STANDARD);
            });
        } else if (modul === 'Spezial') {
            this.jsonFilesApiService.getJsonFile(jsonFileName).subscribe((result: any) => {
                this.displayVhvKaution(result, KreditlinieRuledProduktlinien.SPEZIAL);
            });
        } else {
            this.displayIndividual();
        }
    }

    private handleKtvPremium(vsnr: string) {
        if (!this.isVSNRMatching(vsnr, /^58296[0-9]*0000/)) {
            const jsonFileName = this.appConstants.jsonBusinessFiles.kreditliniePremium;
            this.jsonFilesApiService.getJsonFile(jsonFileName).subscribe((result: any) => {
                this.displayStatic(result);
            });
        } else {
            this.displayIndividual();
        }
    }

    private handleKtvGewerbeUndIndustrie() {
        const jsonFileName = this.appConstants.jsonBusinessFiles.kreditlinieSpezial;
        this.jsonFilesApiService.getJsonFile(jsonFileName).subscribe((result: any) => {
            this.displayStatic(result);
        });
    }

    private handleSonstigeVertragsart(vsnr: string) {
        if (this.isVSNRMatching(vsnr, /^58270[0-9]*0000/)) {
            this.displayKeineErhoehung();
        } else {
            this.displayIndividual();
        }
    }

    private isVSNRMatching(vsnr: string, regex: RegExp): boolean {
        return regex.test(vsnr);
    }

    private loadVertrag(vertragName: string): void {
        this.vertraegeService.getVertrag(vertragName).subscribe({
            next: (vertragData) => {
                this.model.currVertrag = vertragData;
                this.getOptions(vertragData.vertragsart, vertragData.modul, vertragData.vertragHandle.vsnrNumerisch);
            },
            error: () => {
                this.router.navigate(['./kreditportal/vertraege']);
            }
        });
    }

    gotoVertrag(): void {
        this.router.navigate(['./kreditportal/vertraege/', this.model.currVertrag.vertragHandle.vsnrNumerisch]);
    }

    validateField(field: ValidatorInput): void {
        this.model.validateField(field);
    }

    private sendKreditlinienErhoehung(css: string, request: any): void {
        const elemIdentifier = 'printDialogue';
        const printContents = document.getElementById(elemIdentifier).innerHTML;

        request['archivDokument'] = `
          <html lang="de">
              <head>
                  <title>VHV Kreditportal</title>
                  <meta http-equiv="Content-Type" content="text/html;charset=UTF-8">
                  <style>
                      ${css}
                  </style>
              </head>
              <body>
                  ${printContents}
              </body>
          </html>`;

        this.kreditlinieApiService.postErhoeheKreditlinie(request).subscribe(() => {
            this.page2 = false;
            this.page3 = true;
            console.debug('Versand der Kreditlinien-Erhöhung erfolgreich', this);
        });
    }

    private sendKreditlinienErhoehungV2(): void {
        const erhoehungsBeitrag = this.determineErhoehungsbeitrag();
        const firmenname = this.assembleFirmenname();

        let modulAlt = null;
        if (this.model.currVertrag.modul) {
            modulAlt = this.model.currVertrag.modul.toUpperCase();
        }

        let modulNeu = null;
        if (this.model.produktlinieAuswahl) {
            modulNeu = this.model.produktlinieAuswahl.toUpperCase();
        }

        const request: ErhoeheKreditlinieRequest = {
            vsnr: this.model.currVertrag.vertragHandle.vsnrNumerisch,
            aktuellerRahmen: this.model.currVertrag.rahmenBetrag.toString(),
            erhoehungsBeitrag,
            modulAlt,
            modulNeu,
            erfassungsJahr: this.model.geschaeftsjahr.value,
            firmenname
        };

        this.kreditlinieApiService.postErhoeheKreditlinie(request).subscribe(() => {
            this.page2 = false;
            this.page3 = true;
            console.debug('Versand der Kreditlinien-Erhöhung erfolgreich', this);
        });
    }

    private determineErhoehungsbeitrag(): string {
        let erhoehungsBeitrag = 0;

        if (this.model.staticTable || this.model.vhvKautionTable) {
            erhoehungsBeitrag = this.model.getDifferenzStatic()
        } else if (this.model.ruledData) {
            erhoehungsBeitrag = this.model.buergschaftslinieEingabe.value;
        } else if (this.model.individual) {
            erhoehungsBeitrag = this.model.buergschaftbetrag.value;
        } else {
            throw new Error('Illegal State, keine Erhöhungsart verfügbar');
        }

        if (!erhoehungsBeitrag) {
            return '0';
        }

        return erhoehungsBeitrag.toString();
    }

    private assembleFirmenname(): string {
        let firmenname = '';

        if (this.partner.vornameOderName2.value) {
            firmenname += this.partner.vornameOderName2.value;
        }

        if (firmenname && this.partner.nachnameOderName1.value) {
            firmenname += ' ';
        }

        if (this.partner.nachnameOderName1.value) {
            firmenname += this.partner.nachnameOderName1.value;
        }

        return firmenname;
    }

}
